/**
 * successionScore.utils.js — Engine D: Succession Plan Score.
 *
 * A single 0-100 score per role expressing how good that role's succession
 * coverage actually is — not just "does a successor exist" but "how ready
 * are they." Built on top of Engine C's (SBI) scores for the role's top 3
 * successors, weighted by readiness category so a role backed by 3
 * Ready-Now candidates scores far higher than one backed by 3 people who'd
 * need a year+ to be ready.
 *
 * Formula: score = sum(SRI_i * weight_i) / sum(weight_i), over however many
 * of the top 3 successors exist (0 successors -> score is null, rendered as
 * "No Data" — never a misleading 0, since 0 would look like "bad" rather
 * than "no candidates to score at all").
 *
 * Readiness bands (by SRI vs. the two admin-configurable succession
 * thresholds — see useTalentMobilitySettingsStore.js):
 *   Ready Now        (SRI >= readyNow)                     weight 5
 *   Ready in 1 Year   (SRI >= readyOneYear, < readyNow)     weight 3
 *   No immediate successor (SRI < readyOneYear)             weight 2
 */

export const SUCCESSION_WEIGHTS = {
  readyNow: 5,
  readyOneYear: 3,
  noImmediate: 2,
};

export const getReadinessBand = (sri, thresholds) => {
  if (sri >= thresholds.readyNow) return "readyNow";
  if (sri >= thresholds.readyOneYear) return "readyOneYear";
  return "noImmediate";
};

export const READINESS_BAND_LABELS = {
  readyNow: "Ready Now",
  readyOneYear: "Ready in 1 Year",
  noImmediate: "No Immediate Successor",
};

/**
 * @param {Array<{sri:number}>} top3Candidates - the role's top successors (by SRI), already sliced to at most 3
 * @param {{readyNow:number, readyOneYear:number}} thresholds
 * @returns {number|null} 0-100 succession score, or null if no candidates
 */
export const computeSuccessionScore = (top3Candidates, thresholds) => {
  if (!top3Candidates || top3Candidates.length === 0) return null;

  let numerator = 0;
  let denominator = 0;
  for (const candidate of top3Candidates) {
    const band = getReadinessBand(candidate.sri, thresholds);
    const weight = SUCCESSION_WEIGHTS[band];
    numerator += candidate.sri * weight;
    denominator += weight;
  }
  if (denominator === 0) return null;
  return Math.round((numerator / denominator) * 10) / 10;
};

export const getSuccessionScoreColor = (score) => {
  if (score == null) return "default";
  if (score >= 80) return "success";
  if (score >= 60) return "warning";
  return "error";
};
